import React, { useEffect, useMemo, useState } from 'react';
import {
  selectAssets,
  selectRootAssetUuid,
  selectSettingsData,
} from 'src/redux/configuration/configuration.selectors';

import { BaseButton } from 'src/components/BaseButton';
import { BaseButtonSquare } from 'src/components/BaseButtonSquare';
import { ChartSimulationProgress } from 'src/components/_charts/ChartSimulationProgress';
import { TSimulationProgressModalExpandedProps } from './SimulationProgressModalExpanded.types';
import { UTCMoment } from 'src/utils/UTCMoment';
import s from './SimulationProgressModalExpanded.module.scss';
import { useSelector } from 'react-redux';

export const SimulationProgressModalExpanded: React.FC<TSimulationProgressModalExpandedProps> = ({
  simulationStatusTitle,
  simulationProgress,
  runButtonState,
  runButtonOptions,
  stopButtonOptions,
  toggleExpanded,
  detailsData,
}) => {
  const rootAssetUuid = useSelector(selectRootAssetUuid);
  const assets = useSelector(selectAssets);
  const settingsData = useSelector(selectSettingsData);

  const assetsCount = useMemo(() => {
    const assetsInsideCommunity = Object.keys(assets).filter(
      (uuid) => assets[uuid].uuid !== rootAssetUuid && assets[uuid].parentUuid !== rootAssetUuid,
    );
    return assetsInsideCommunity.length;
  }, [assets, rootAssetUuid]);

  const runButtonTheme = useMemo(() => {
    switch (runButtonState) {
      case 'run':
      case 'resume':
      case 'rerun':
        return 'primary';
      case 'pause':
        return 'secondary';
      case 'processing':
        return 'grey';
    }
  }, [runButtonState]);

  const timeElapsed = useMemo(() => {
    if (!simulationProgress) return null;
    const seconds = simulationProgress.elapsedTimeSeconds;
    if (seconds > 60)
      return Math.round(seconds / 60) + ' min ' + (seconds % 60 ? (seconds % 60) + ' sec' : '');
    return seconds + ' sec';
  }, [simulationProgress]);

  const simulationTime = useMemo(() => {
    const startDate = UTCMoment.utc(settingsData.startDate);
    const endDate = UTCMoment.utc(settingsData.endDate);
    const dayDiff = endDate.diff(startDate, 'd');
    const currentDay = Math.min(
      dayDiff,
      Math.ceil((dayDiff / 100) * (simulationProgress?.percentageCompleted || 0)),
    );
    return `${currentDay} / ${dayDiff}`;
  }, [settingsData.endDate, settingsData.startDate, simulationProgress?.percentageCompleted]);

  return (
    <div className={s.container}>
      <div className={s.header}>
        <div>
          <span>{simulationStatusTitle}</span>
          {!!simulationProgress?.percentageCompleted && (
            <span>{simulationProgress.percentageCompleted}%</span>
          )}
        </div>
        <div>
          <div className={s.simActionButtons}>
            <BaseButtonSquare
              size="2"
              theme={stopButtonOptions.disabled ? 'flat-gray-light' : 'flat-red'}
              icon={stopButtonOptions.icon}
              svgSize="3"
              disabled={stopButtonOptions.disabled}
              onClick={() => stopButtonOptions.onClick && stopButtonOptions.onClick()}
            />
            <BaseButton
              size="small"
              theme={runButtonTheme}
              className={s.actionButton}
              disabled={runButtonOptions.disabled}
              onClick={() => runButtonOptions.onClick && runButtonOptions.onClick()}>
              {runButtonOptions.title}
            </BaseButton>
          </div>
          {/* <div className={s.graphPills}>hey
            <BasePills />
          </div> */}
          <div className={s.modalControlButtons}>
            <BaseButtonSquare
              size="2"
              theme="flat-gray-light"
              icon="expand"
              svgSize="3"
              onClick={toggleExpanded}
            />
            <BaseButtonSquare size="2" theme="flat-gray-light" icon="close" svgSize="3" />
          </div>
        </div>
      </div>
      <div className={s.content}>
        <div className={s.detailsAndControls}>
          <ul>
            <li>
              <span>Simulation time:</span> {simulationTime} days
            </li>
            {!!timeElapsed && (
              <li>
                <span>Time elapsed:</span> {timeElapsed}
              </li>
            )}
            <li>
              <span>Nodes added:</span> {assetsCount}/1000
            </li>
          </ul>
        </div>
        <div className={s.graph}>
          <ChartSimulationProgress graphHeight={100} />
        </div>
      </div>
    </div>
  );
};
